// Imports
import { Add, ArrowBack, FileDownload, HighlightOff, MenuBookOutlined, MoreVert, ToggleOff, ToggleOn, Tune } from '@mui/icons-material';
import { Button, Divider, Drawer, Fab, FormControlLabel, FormGroup, IconButton, InputAdornment, Menu, MenuItem, Radio, Select, Slide, Snackbar, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHubSelection } from '../../features/hubSelection';
import { GobikesApi } from '../../config/Apis';
import { millisToHumanReadableDuration, parseErrorForAxios, billingCycleDisplayLabel } from '../utils/CommonUtils';
import { getAuthCookie } from '../auth/AuthUtil';
import { TabContext, TabPanel } from '@mui/lab';
import { BookingStatus, BookingType, OrderModel, VendorOrderMode } from '../utils/Models';
import { BookingsDataGrid, bookingStatusToBgColor, bookingStatusToColor } from './BookingsDataGrid';
import { BookingDetails, RideStatusToText } from './BookingDetails';
import { Colors, DEFAULT_LOADER_STYLES } from '../../config/Constants';
import { DashboardLoader } from '../utils/LocalStateUtils';
import { CreateBooking } from './CreateBooking';
import { getNavData, setNavData, setNavSelection } from '../../features/navSelection';
import { DateTime } from 'luxon';
import { CSVLink } from "react-csv";
import { getVendorHubs, getVendorProfile } from '../../features/vendorProfile';

// Types and constants
type BookingsView = 'all' | 'individual' | 'create';
const defaultView = 'all';
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  view: BookingsView;
  orderData?: OrderModel;
}
export enum BookingStateFilters {
  ALL = "All",
  TODAY_PICKUPS = "Today's Pickups",
  TODAY_PENDING_PICKUPS = "Today's Pending Pickups",
  TODAY_DROPS = "Today's Drops",
  TODAY_PENDING_DROPS = "Today's Pending Drops",
  TOMORROW_PICKUPS = "Tomorrow's Pickups",
  TOMORROW_DROPS = "Tomorrow's Drops",
  UPCOMING_TIME = 'Upcoming (by Time)',
  ONGOING_TIME = "Ongoing (by Time)",
  UPCOMING_STATUS = 'Pending Pickups / Upcoming (by Status)',
  ONGOING_STATUS = "Pending Drops / Ongoing (by Status)",
  CANCELLED = 'Cancelled',
  EXTENDED = 'Extended',
  COMPLETED_TIME = 'Completed (by Time)',
  COMPLETED_STATUS = 'Completed (by Status)',
  SUBSCRIPTION_ORDERS = 'Subscription Orders',
  SUBSCRIPTION_HEALTHY = 'Healthy',
  SUBSCRIPTION_PAYMENT_DUE = 'Payment Due',
  SUBSCRIPTION_OVERDUE = 'Overdue'
}
export enum SearchFilters {
  BOOKING_ID = "Booking ID",
  CUSTOMER_NAME = "Customer Name",
  PHONE_NUMBER = "Phone",
  RC_NUMBER = "Bike Number"
}
interface DataExportFormat {
  bookingId: string;
  bookingTime: string;
  pickupTime: string;
  dropTime: string;
  duration: string;
  customerName: string;
  bikeModel: string;
  bikeNumber: string;
  totalAmount: string;
  vendorCommission: string;
  paymentStatus: string;
  bookingStatus: string;
  rideStatus: string;
}
const TableHeaders: { label: string, key: keyof DataExportFormat }[] = [
  { label: 'Booking ID', key: 'bookingId' },
  { label: 'Booking Date and Time', key: 'bookingTime' },
  { label: 'Pickup Date and Time', key: 'pickupTime' },
  { label: 'Drop Date and Time', key: 'dropTime' },
  { label: 'Duration', key: 'duration' },
  { label: 'Customer Name', key: 'customerName' },
  { label: 'Bike Model', key: 'bikeModel' },
  { label: 'Bike Numbers', key: 'bikeNumber' },
  { label: 'Total Amount', key: 'totalAmount' },
  { label: 'Vendor Commission', key: 'vendorCommission' },
  { label: 'Payment Status', key: 'paymentStatus' },
  { label: 'Booking Status', key: 'bookingStatus' },
  { label: 'Ride Status', key: 'rideStatus' },
]

// Main app component
function Bookings(props: {
  window?: () => Window;
  drawerWidth: number
}) {

  // Local state
  const [localState, setLocalState] = useState({ view: defaultView as BookingsView } as LocalState);
  const [ordersData, setOrdersData] = useState({ all: [] as OrderModel[] });
  const [csvData, setCsvData] = useState<DataExportFormat[]>([]);
  const [hubOrdersCache, setHubOrdersCache] = useState<{ [hubId: number]: OrderModel[] }>({});
  const [toastState, setToastState] = useState<{ open: boolean, message?: string }>({ open: false });
  const [searchState, setSearchState] = useState<{ field: SearchFilters, keyword: string }>({ field: SearchFilters.BOOKING_ID, keyword: '' });
  const [filterState, setFilterState] = useState<{ open: boolean, selections: { [k in BookingStateFilters]?: boolean } }>({ open: false, selections: { [BookingStateFilters.ALL]: true } });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Local variables based on state
  const orderDetailsView = localState.view !== 'all';
  const isSubscriptionView = Boolean(
    filterState.selections[BookingStateFilters.SUBSCRIPTION_ORDERS] ||
    filterState.selections[BookingStateFilters.SUBSCRIPTION_HEALTHY] ||
    filterState.selections[BookingStateFilters.SUBSCRIPTION_PAYMENT_DUE] ||
    filterState.selections[BookingStateFilters.SUBSCRIPTION_OVERDUE]
  );
  const toggleSubscriptionView = () => {
    setFilterState({
      open: false,
      selections: isSubscriptionView ? { [BookingStateFilters.ALL]: true } : { [BookingStateFilters.SUBSCRIPTION_ORDERS]: true }
    });
  };

  // Global state
  const selectedHub = useSelector(getHubSelection);
  const vendorHubs = useSelector(getVendorHubs);
  const profile = useSelector(getVendorProfile);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));
  const navDataOrder = (useSelector(getNavData))?.orderId || null;

  // Set nav state
  useEffect(() => {
    dispatch(setNavSelection(2));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the mobile kebab menu if the viewport crosses into desktop (its trigger button gets hidden, but the portaled menu wouldn't otherwise know to close)
  useEffect(() => {
    setMenuAnchor(null);
  }, [isPhone]);

  // Fetch orders for a single hub from the API
  const fetchHubOrders = async (hubId: number, authCookie: string): Promise<OrderModel[]> => {
    const { data } = await GobikesApi.get(`/vendors/hubs/${hubId}/orders?loadBikes=true&loadExtensions=true&loadSubscriptionInvoices=true&loadReviews=true&loadUserOrderCounts=true&loadVendorCustomerStats=true`, { headers: { 'vendor-token': authCookie } })
    return data.data;
  }

  // Build CSV export rows from an order list
  const toCsvData = (orderList: OrderModel[]): DataExportFormat[] => orderList.map(o => ({
    bookingId: o.id.toString(),
    bookingTime: DateTime.fromMillis(new Date(o.createdAt).getTime()).toLocaleString(DateTime.DATETIME_MED),
    pickupTime: DateTime.fromMillis(Number(o.rideStartScheduled)).toLocaleString(DateTime.DATETIME_MED),
    dropTime: DateTime.fromMillis(Number(o.rideEndScheduled)).toLocaleString(DateTime.DATETIME_MED),
    duration: millisToHumanReadableDuration(Number(o.rideEndScheduled) - Number(o.rideStartScheduled)),
    customerName: `${o.user?.firstName} ${o.user?.lastName}`,
    bikeModel: `${o?.bikes?.[0].brand} ${o?.bikes?.[0].name}`,
    bikeNumber: o.bikeDetails ? o.bikeDetails.map(b => b.rcNumber).join(',') : '',
    totalAmount: (o.totalBikeAmount + o.totalAccessoryAmount).toFixed(2),
    vendorCommission:
      (o.finalVendorAmount - ((Math.max(o.refundAmounts?.vendorRefundSplitAmount || 0, 0) + Math.max(o.refundAmounts?.vendorDebitAmount || 0, 0)) + o.offlineCashAmount)).toFixed(2)
    ,
    paymentStatus: o.paymentDone ? 'Yes' : 'No',
    bookingStatus: o.bookingStatus,
    rideStatus: RideStatusToText[o.rideStatus],
  }));

  // Load orders on first load / on hub change
  const loadOrders = (openOrderId?: number, toastState?: { open: boolean, message?: string }) => {
    const authCookie = getAuthCookie();
    if (!selectedHub?.id || !authCookie) return;
    setLocalState({ loading: true, view: defaultView });
    (async () => {
      try {
        const orderList = await fetchHubOrders(selectedHub.id, authCookie);
        setHubOrdersCache(prev => ({ ...prev, [selectedHub.id]: orderList }));
        if (toastState)
          setToastState(toastState)
        if (openOrderId) {
          const filteredOrder = orderList.find(o => o.id === openOrderId);
          if (filteredOrder) {
            setLocalState({ loading: false, view: 'individual', orderData: filteredOrder });
            return;
          }
        } else if (navDataOrder) {
          const filteredOrder = orderList.find(o => o.id.toString() === navDataOrder.toString());
          if (filteredOrder) {
            dispatch(setNavData({}));
            setLocalState({ loading: false, view: 'individual', orderData: filteredOrder });
            return;
          }
        }
        setLocalState({ view: defaultView });
      } catch (e) {
        setLocalState({ error: true, message: parseErrorForAxios(e), view: defaultView });
      }
    })();
  }
  useEffect(loadOrders, [selectedHub]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const authCookie = getAuthCookie();
    if (!searchState.keyword || !authCookie || vendorHubs.length <= 1) return;
    const missingHubs = vendorHubs.filter(h => !hubOrdersCache[h.id]);
    if (!missingHubs.length) return;
    (async () => {
      const fetched = await Promise.all(missingHubs.map(async h => [h.id, await fetchHubOrders(h.id, authCookie)] as const));
      setHubOrdersCache(prev => {
        const next = { ...prev };
        fetched.forEach(([id, orders]) => { next[id] = orders; });
        return next;
      });
    })();
  }, [searchState.keyword, vendorHubs, hubOrdersCache]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchingAcrossHubs = Boolean(searchState.keyword) && vendorHubs.length > 1;
  useEffect(() => {
    const orders = searchingAcrossHubs
      ? vendorHubs.flatMap(h => hubOrdersCache[h.id] || [])
      : (selectedHub?.id ? (hubOrdersCache[selectedHub.id] || []) : []);
    setOrdersData({ all: orders });
    setCsvData(toCsvData(orders));
  }, [hubOrdersCache, searchingAcrossHubs, selectedHub, vendorHubs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading view
  let mainContent = <div></div>;
  if (localState.loading) {
    mainContent = <DashboardLoader
      {...localState}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // Error view
  else if (localState.error) {
    mainContent = <DashboardLoader
      {...localState}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // All bookings view
  else if (localState.view === 'all') {
    mainContent = <Fragment>

      {/* Filter view */}
      {renderFilterDrawer()}

      {/* Search box for non-phone screens */}
      {!isPhone ? renderFilterHeader() : ''}

      {/* Bookings container */}
      <Box className='gbk-tabs gbk-tab-with-header' sx={{ typography: 'body1', m: '30px 0 0 0' }}>

        {/* Phone FAB */}
        <Box className="phone-fab">
          {profile.subscriptionEnabled ? (
            <Fragment>
              <Button
                className="thick-border low-pad"
                variant={isSubscriptionView ? 'contained' : 'outlined'}
                onClick={toggleSubscriptionView}
                sx={{ minWidth: 0 }}
              >
                {isSubscriptionView ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}&nbsp;Subscription
              </Button>
              &nbsp;
            </Fragment>
          ) : ''}
          <IconButton className="thick-border" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ border: '2px solid', borderColor: 'divider' }}>
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={() => setMenuAnchor(null)}>
              <CSVLink
                data={csvData}
                headers={TableHeaders}
                filename={`GoBikes_Orders_${DateTime.now().toLocaleString(DateTime.DATETIME_MED).replaceAll(' ', '_').replaceAll(',', '')}.csv`}
                asyncOnClick={true}
                style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <FileDownload fontSize="small" /> CSV
              </CSVLink>
            </MenuItem>
            {profile.orderMode === VendorOrderMode.ALL ? (
              <Fragment>
                <Divider />
                <MenuItem onClick={() => { setMenuAnchor(null); setLocalState({ view: 'create' }); }}>
                  <Add fontSize="small" />&nbsp;Create Booking
                </MenuItem>
              </Fragment>
            ) : ''}
          </Menu>

        </Box>
        {isPhone ? <br /> : ''}

        {/* Bookings selection tabs */}
        <TabContext value={"0"}>

          {/* Phone search box */}
          {isPhone ? renderFilterHeader() : ''}

          {/* Bookings grids */}
          {Object.keys(ordersData).map((a, index) => (
            <TabPanel style={{ padding: 0, height: 'calc(100% - 10px)' }} value={index.toString()} key={index} >
              <BookingsDataGrid
                ordersList={ordersData[a as keyof typeof ordersData]}
                setParentState={setLocalState}
                containerClass={'gbk-grid-parent smaller'}
                searchFilters={searchState}
                stateFilters={filterState.selections}
                hubId={selectedHub?.id}
                hubsById={vendorHubs.reduce((acc, h) => ({ ...acc, [h.id]: h }), {} as { [hubId: number]: typeof vendorHubs[0] })}
                showHubColumn={searchingAcrossHubs}
                refreshOrders={() => loadOrders()}
                setToastState={setToastState}
              />
            </TabPanel>
          ))}
        </TabContext>
      </Box >
    </Fragment>
  }

  // Individual booking view
  else if (localState.view === 'individual') {
    if (!localState.orderData) {
      setLocalState({ error: true, message: 'Invalid order data', view: defaultView });
    } else {
      mainContent = (
        <BookingDetails
          order={localState.orderData}
          backFn={(refreshData: boolean, orderId?: number, toastState?: { open: boolean, message?: string }) => {
            if (refreshData)
              loadOrders(orderId, toastState)
            else if (orderDetailsView)
              setLocalState({ view: defaultView, orderData: undefined })
          }}
          setToastState={setToastState}
          drawerWidth={props.drawerWidth}
        />)
    }
  }

  // Create booking view
  else if (localState.view === 'create') {
    mainContent = (
      <CreateBooking
        backFn={(refreshData: boolean) => {
          if (refreshData)
            loadOrders(undefined, toastState)
          if (orderDetailsView)
            setLocalState({ view: defaultView, orderData: undefined })
        }}
        drawerWidth={props.drawerWidth}
      />)
  }

  // Default view
  else {
    mainContent = <div></div>;
  }

  // Return DOM
  return (
    <Box sx={{ mt: { xs: '50px', md: 0 }, ml: { xs: 0, md: `${props.drawerWidth}px` }, width: { xs: '100%', md: `calc(100% - ${props.drawerWidth}px)` }, height: '100%' }}>
      <div className={`gbk-tabcontent ${localState.view === 'all' ? '' : 'allow-scroll'}`}>

        {/* Desktop action buttons - single flex row so button widths never overlap each other */}
        {
          localState.view === 'all' ? (
            <Box sx={{ position: 'absolute', top: '40px', right: '40px', display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '16px' }}>
              {profile.subscriptionEnabled && (
                <Button
                  onClick={toggleSubscriptionView}
                  variant={isSubscriptionView ? 'contained' : 'outlined'}
                  color="primary"
                  sx={{ height: '48px', px: 2.5, fontWeight: 600, textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {isSubscriptionView ? <ToggleOn /> : <ToggleOff />}&nbsp;Subscription
                </Button>
              )}
              <CSVLink
                data={csvData}
                headers={TableHeaders}
                filename={`GoBikes_Orders_${DateTime.now().toLocaleString(DateTime.DATETIME_MED).replaceAll(' ', '_').replaceAll(',', '')}.csv`}
                asyncOnClick={true}
              >
                <Fab className='gbk-fab' variant='extended' color='primary'> <FileDownload />csv</Fab>
              </CSVLink>
              {profile.orderMode === VendorOrderMode.ALL && (
                <Fab onClick={() => { setLocalState({ view: 'create' }) }} className='gbk-fab' variant='extended' color='primary'>
                  <Add /> Create Booking
                </Fab>
              )}
            </Box>
          ) : ''
        }


        {/* Show back button when not in list view */}
        {localState.view !== 'all' ? (
          <Box className="phone-fab">
            <Button className="thick-border low-pad" size='small' variant="outlined" onClick={e => setLocalState({ view: defaultView, orderData: undefined })}> <ArrowBack /> Back</Button>
          </Box>
        ) : ''}

        {/* Show booking status in phone nav header when in individual view */}
        {localState.view === 'individual' ? (
          <Box className="phone-fab roboto" sx={{ right: '95px' }} >
            {localState.orderData && localState.orderData?.bookingStatus ? (
              <div
                style={{
                  border: `1px solid ${bookingStatusToBgColor[localState.orderData?.bookingStatus]}`,
                  color: bookingStatusToColor[localState.orderData?.bookingStatus],
                  background: bookingStatusToBgColor[localState.orderData?.bookingStatus],
                  borderRadius: '6px',
                  padding: '2px 4px',
                  width: '100px',
                  textAlign: 'center',
                  display: 'inline-block',
                  fontSize: '12px'
                }}
              >
                {localState.orderData?.bookingStatus} <br />
                <b>Order ID: {localState.orderData.id}</b>
              </div >
            ) : ''}
          </Box>
        ) : ''}

        {/* Subscription billing info - sits below the fixed app header (not inside the status pill above,
            which is too small/clipped by the header bar to hold this much text) */}
        {localState.view === 'individual' && localState.orderData?.bookingType === BookingType.SUBSCRIPTION && localState.orderData.bookingStatus !== BookingStatus.PENDING ? (
          <Box
            className="roboto"
            sx={{
              display: { xs: 'flex', md: 'none' },
              position: 'fixed',
              top: '58px',
              left: '8px',
              right: '8px',
              zIndex: 1100,
              border: `1px solid ${bookingStatusToBgColor[localState.orderData.bookingStatus]}`,
              background: bookingStatusToBgColor[localState.orderData.bookingStatus],
              color: bookingStatusToColor[localState.orderData.bookingStatus],
              borderRadius: '6px',
              padding: '4px 8px',
              fontSize: '11px',
              justifyContent: 'center',
              gap: '10px',
              flexWrap: 'wrap'
            }}
          >
            <span>Outstanding: <b>₹{Number(localState.orderData.outstandingAmount || 0).toFixed(0)}</b></span>
            <span>Next Billing: <b>{localState.orderData.nextBillingDate ? DateTime.fromMillis(Number(localState.orderData.nextBillingDate)).toFormat('MMM d, yyyy') : '-'}</b></span>
            <span>Cycle: <b>{localState.orderData.billingCycle ? billingCycleDisplayLabel[localState.orderData.billingCycle] : '-'}</b></span>
          </Box>
        ) : ''}

        {/* Header */}
        <Box className='tab-header' sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', paddingRight: '64px' }}>

          {/* Icon */}
          <div
            className={`tab-header-fab ${orderDetailsView ? 'clickable' : ''}`}
            onClick={() => { if (orderDetailsView) setLocalState({ view: defaultView, orderData: undefined }) }} >
            {
              orderDetailsView
                ? <ArrowBack fontSize="large" />
                : <MenuBookOutlined fontSize="large" />
            }
          </div>

          {/* Text */}
          <Typography>
            <span style={{ ...(orderDetailsView && { color: Colors.primary }) }}>
              {orderDetailsView ? 'Order ID:' : 'Bookings'}
            </span>
            {orderDetailsView ? ` ${localState.orderData?.id || 'New'}` : null}
          </Typography>

          {/* Booking status badge */}
          {orderDetailsView && localState.orderData?.bookingStatus ? (
            <span
              className="desktop-header-status-badge"
              style={{
                color: bookingStatusToColor[localState.orderData.bookingStatus],
                background: bookingStatusToBgColor[localState.orderData.bookingStatus],
              }}
            >
              {localState.orderData.bookingStatus}
            </span>
          ) : null}

          {/* Outstanding amount & next billing date - subscription orders only */}
          {orderDetailsView && localState.orderData?.bookingType === BookingType.SUBSCRIPTION && localState.orderData?.billingCycle ? (
            <Box sx={{ marginLeft: 'auto', fontSize: '14px', whiteSpace: 'nowrap' }}>
              <span style={{ color: '#888', fontWeight: 700 }}>Outstanding: </span>
              <strong className="roboto" style={{ color: Colors.primary }}>₹ {Number(localState.orderData.outstandingAmount || 0).toFixed(2)}</strong>
              <span style={{ margin: '0 10px', color: '#ccc' }}>|</span>
              <span style={{ color: '#888', fontWeight: 700 }}>Next Billing: </span>
              <strong>{localState.orderData.nextBillingDate ? DateTime.fromMillis(Number(localState.orderData.nextBillingDate)).toFormat('MMM d, yyyy') : '-'}</strong>
              <span style={{ margin: '0 10px', color: '#ccc' }}>|</span>
              <span style={{ color: '#888', fontWeight: 700 }}>Cycle: </span>
              <strong>{billingCycleDisplayLabel[localState.orderData.billingCycle]}</strong>
            </Box>
          ) : null}
        </Box>

        {/* Toast */}
        <Snackbar
          open={toastState.open}
          message={toastState.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: isPhone ? 'bottom' : 'top', horizontal: isPhone ? 'center' : 'right' }}
          sx={{ margin: isPhone ? '80px 50px' : '' }}
          TransitionComponent={(props) => (
            <Slide {...props} direction={isPhone ? 'up' : 'left'} />
          )}
          onClose={
            (_, reason?: string) => {
              if (reason === 'clickaway') {
                return;
              }
              setToastState({ open: false })
            }
          }
        />

        {/* Main content */}
        {mainContent}

      </div >
    </Box >
  )

  // Render search box
  function renderFilterHeader() {
    return (
      <Box className="booking-search-container" sx={{ mt: { xs: '0px', md: '30px' }, ml: { xs: '15px', md: '0' } }}>
        <TextField
          className='input-border-grey input-no-pad-right'
          sx={{
            backgroundColor: '#fff',
            width: { xs: 'calc(100% - 128px)', md: 'calc(100% - 200px)' }
          }}
          label={undefined}
          size={'small'}
          value={searchState.keyword}
          placeholder='Search...'
          onChange={(e) => {
            setSearchState({ ...searchState, keyword: e.target.value })
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Select
                  defaultValue={SearchFilters.BOOKING_ID}
                  className='select-no-rad-left select-low-pad select-bg-grey select-right-adornment'
                  size='small'
                  onChange={(e) => {
                    setSearchState({ ...searchState, field: e.target.value as SearchFilters })
                  }}
                  style={{ borderLeftColor: '#ddd' }}
                >
                  {Object.values(SearchFilters).map(f => (
                    <MenuItem key={f} value={f}>{f}</MenuItem>
                  ))}
                </Select>
              </InputAdornment>
            )
          }}
        />
        <Button
          variant='outlined'
          className='no-text-transform'
          sx={{
            width: { xs: '100px', md: '140px' },
            ml: { xs: '10px', md: '20px' },
            height: '40px',
            borderColor: Colors.greyDark3,
            color: Colors.greyDark2,
            background: '#fff',
            fontWeight: 400,
            fontSize: '16px',
            padding: '10px 5px'
          }}
          onClick={() => setFilterState({ ...filterState, open: true })}
        >
          Filter &nbsp; <Tune />
        </Button>
      </Box>
    )
  }

  // Filter modal
  function renderFilterDrawer() {
    return (
      <Drawer
        className='gbk-drawer'
        anchor='bottom'
        open={filterState.open}
        onClose={() => {
          setFilterState({ ...filterState, open: false })
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh', padding: { xl: '0 280px' } }}>
          {/* Sticky header */}
          <Box className="gbk-filter-header">
            <Typography className='poppins' variant='h6'>
              Filter Bookings
              <span
                className='gbk-icon-span clickable large'
                style={{ float: 'right', paddingTop: '4px' }}
                onClick={() => setFilterState({ ...filterState, open: false })}
              >
                <HighlightOff />
              </span>
            </Typography>
            <hr style={{ borderColor: Colors.primary, borderTop: 0, margin: '8px 0 0' }} />
          </Box>
          {/* Scrollable options */}
          <Box className="gbk-filter-body">
            <FormGroup>
              {(isSubscriptionView
                ? [BookingStateFilters.SUBSCRIPTION_ORDERS, BookingStateFilters.SUBSCRIPTION_HEALTHY, BookingStateFilters.SUBSCRIPTION_PAYMENT_DUE, BookingStateFilters.SUBSCRIPTION_OVERDUE]
                : Object.values(BookingStateFilters).filter(f => ![BookingStateFilters.SUBSCRIPTION_ORDERS, BookingStateFilters.SUBSCRIPTION_HEALTHY, BookingStateFilters.SUBSCRIPTION_PAYMENT_DUE, BookingStateFilters.SUBSCRIPTION_OVERDUE].includes(f))
              ).map(f => (
                <Fragment key={f}>
                  <FormControlLabel
                    label={f === BookingStateFilters.SUBSCRIPTION_ORDERS ? 'All' : f}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' }, mb: '-14px' }}
                    control={
                      <Radio
                        size="small"
                        checked={filterState.selections[f] || false}
                        onChange={(e) => {
                          setFilterState({ open: false, selections: { [f]: e.target.checked } })
                        }}
                        sx={{ color: Colors.primary, py: '1px' }}
                      />
                    }
                  />
                </Fragment>
              ))}
            </FormGroup>
          </Box>
        </Box>
      </Drawer>
    )
  }
}

// /**
//  * Utility function to download csv of order data
//  */
// function downloadCsv(data: OrderModel[]) {
//   console.log(data);

//   const url = window.URL.createObjectURL(new Blob([data]))
//   const link = document.createElement('a')
//   link.href = url
//   const fileName = `order_list_${DateTime.fromMillis(new Date().getTime()).toString()}.csv`;
//   link.setAttribute('download', fileName)
//   document.body.appendChild(link)
//   link.click()
//   link.remove()
// }

export default Bookings;
